import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { CUSTOM_SCROLLBAR } from "@/lib/constants";
import NoSqlAttributesEditor from "./nosql-attributes-editor";
import {
  NoSqlOntologyIndex,
  useCreateNoSqlIndex,
  useUpdateNoSqlIndex,
} from "@/service-hooks/use-get-nosql-ontology-service";

interface NoSqlIndexDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectUuid: string;
  dataLakeId: string;
  collectionId: string;
  collectionName?: string;
  existing?: NoSqlOntologyIndex;
  onSaved?: () => void;
}

interface FormState {
  name: string;
  fields: string; // comma-separated in the form
  unique: boolean;
  description: string;
  attributes: string;
}

const EMPTY: FormState = { name: "", fields: "", unique: false, description: "", attributes: "" };

const parseFields = (s: string): string[] =>
  s.split(",").map((f) => f.trim()).filter(Boolean);

export default function NoSqlIndexDialog({
  isOpen,
  onClose,
  projectUuid,
  dataLakeId,
  collectionId,
  collectionName,
  existing,
  onSaved,
}: NoSqlIndexDialogProps) {
  const [form, setForm] = useState<FormState>({ ...EMPTY });
  const createIndex = useCreateNoSqlIndex();
  const updateIndex = useUpdateNoSqlIndex();
  const isEdit = !!existing?.id;
  const isPending = createIndex.isPending || updateIndex.isPending;

  useEffect(() => {
    if (!isOpen) return;
    setForm({
      name: existing?.name ?? "",
      fields: (existing?.fields ?? []).join(", "),
      unique: !!existing?.unique,
      description: existing?.description ?? "",
      attributes: existing?.attributes ?? "",
    });
  }, [isOpen, existing]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const fieldsArray = parseFields(form.fields);

  const handleSubmit = async () => {
    if (!isEdit && !form.name.trim()) return;
    if (fieldsArray.length === 0) return;
    try {
      if (isEdit && existing?.id) {
        // name is the MERGE key and immutable server-side.
        await updateIndex.mutateAsync({
          id: existing.id,
          projectUuid,
          dataLakeId,
          data: {
            fields: fieldsArray,
            unique: form.unique,
            description: form.description.trim() || undefined,
            attributes: form.attributes || undefined,
          },
        });
      } else {
        await createIndex.mutateAsync({
          collectionId,
          projectUuid,
          dataLakeId,
          data: {
            name: form.name.trim(),
            fields: fieldsArray,
            unique: form.unique,
            description: form.description.trim() || undefined,
            attributes: form.attributes || undefined,
          },
        });
      }
      onSaved?.();
      onClose();
    } catch {
      // toast handled in mutation
    }
  };

  const canSubmit = fieldsArray.length > 0 && (isEdit || !!form.name.trim());

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Index" : "Add Index"}
            {collectionName ? ` — ${collectionName}` : ""}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Configure the index fields for the selected NoSQL collection.
          </DialogDescription>
        </DialogHeader>

        <div className={`space-y-4 overflow-y-auto flex-1 pr-1 ${CUSTOM_SCROLLBAR}`}>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">
              Name <span className="text-error">*</span>
            </label>
            <Input
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="e.g. email_unique_idx"
              disabled={isEdit}
              className="bg-surface text-white disabled:opacity-60"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">
              Fields <span className="text-error">*</span>
            </label>
            <Input
              value={form.fields}
              onChange={(e) => update("fields", e.target.value)}
              placeholder="Comma-separated field paths — e.g. email, role"
              className="bg-surface text-white font-mono"
            />
            <p className="text-xs text-muted">
              {fieldsArray.length} field{fieldsArray.length === 1 ? "" : "s"}:{" "}
              {fieldsArray.join(", ") || "—"}
            </p>
          </div>

          <div className="flex items-center justify-between rounded border border-surface-border bg-surface px-3 py-2">
            <div className="text-sm font-medium text-white">Unique</div>
            <Switch
              checked={form.unique}
              onCheckedChange={(v) => update("unique", v)}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">Description</label>
            <Textarea
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              placeholder="What this index is for (optional)"
              className="bg-surface text-white min-h-[60px]"
            />
          </div>

          <NoSqlAttributesEditor
            value={form.attributes}
            onChange={(json) => update("attributes", json)}
            disabled={isPending}
          />
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-surface-border">
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || isPending}
            className="bg-primary hover:bg-primary-dark text-white"
          >
            {isPending ? (isEdit ? "Saving..." : "Adding...") : isEdit ? "Save" : "Add"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
